import {
  ArrowUpOutlined,
  CreditCardOutlined,
  DollarCircleOutlined,
  InfoCircleOutlined,
  RollbackOutlined,
  ScheduleOutlined,
  StopOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { Card, Flex, Typography, Button, ConfigProvider, Form, Input, Radio, Tooltip } from "antd";
import type { CardProps } from "antd";
import type { CSSProperties, ReactNode } from "react";
import { createPortal } from "react-dom";
import { modalButtonsTheme } from "components/modals/ModalChrome";
import ManagePackageSharingModal from "components/modals/ManagePackageSharingModal.component";
import { showCautionConfirm, showDestructiveConfirm } from "components/modals/destructiveConfirm";
import { ICON_TONES } from "shared/theme/color";
import { StatusBadge } from "pages/schedule/component/ScheduleStatusBadge";
import { translate, translateWithVars } from "shared/languages";
import { colors } from "shared/theme/color";
import { CustomerPackage } from "shared/types";
import {
  INACTIVE_REASON_LABELS,
  INACTIVE_REASON_TONE,
  amountPaid,
  formatCurrency,
  getActualRemainingCredits,
  isCustomerPackageExpired,
  PACKAGE_VALIDITY_LABELS,
  PACKAGE_VALIDITY_TONE,
  packageActivation,
  REWARD_LABEL,
  rewardSourceOf,
  SHARED_LABEL,
  UPGRADE_CANCELLED_VOID_REASON,
  UPGRADE_LABEL,
  UPGRADED_STATUS_REASON,
} from "shared/utils";
import { DEFAULT_DATE_FORMAT, formatDate, sameJakartaDay, tzDate } from "shared/utils/dayjs";
import { discountSourceOf, discountSummary } from "shared/utils/purchaseDiscount";
import { DiscountSource, VoidReason } from "shared/types/enum";
import { InactiveReason } from "shared/types/entity";
import UpdateCustomerPackageModal from "./modals/UpdateCustomerPackage.component";
import UpdateCustomerPackageCreditsModal from "./modals/UpdateCustomerPackageCredits.component";
import UpdateCustomerPackagePaymentMethodModal from "./modals/UpdateCustomerPackagePaymentMethod.component";
import {
  SpentVoucherAction,
  VoidCustomerPackageForm,
  useCancelUpgrade,
  useUnvoidCustomerPackage,
  useVoidCustomerPackage,
} from "hooks/mutations/customerpackage";
import { getStatusLogsByCustomerPackageId } from "hooks/queries/customerpackage";
import { useAdminCapabilities } from "hooks/useAdminCapabilities";
import { AdminCapability } from "shared/utils/capabilities";
import { textPx } from "shared/constants/zoom";
import { InclusiveTaxNote } from "components/InclusiveTaxNote.component";
import WarningBox from "components/WarningBox";

const { Title } = Typography;

// Package validity status (fixed English like every status badge).
const ValidTag: React.FC<{ isValid: boolean }> = ({ isValid }) => (
  <StatusBadge
    tone={PACKAGE_VALIDITY_TONE[isValid ? "active" : "inactive"]}
    label={PACKAGE_VALIDITY_LABELS[isValid ? "active" : "inactive"]}
  />
);

// The value type of a fact cell.
const VALUE_TEXT: CSSProperties = {
  fontFamily: "var(--font-display)",
  fontSize: textPx(20),
  fontWeight: 400,
  letterSpacing: "-0.01em",
  lineHeight: 1.3,
  color: colors.ink,
  fontVariantNumeric: "tabular-nums",
};

// One fact of the strip: an eyebrow (navy icon chip + bold uppercase label;
// the chip is the home dashboard's KPI chip, every cell the same navy because
// the cells are siblings and colour is not carrying information here) with
// the cell's action at its right end, then the value with the optional caption
// on the SAME line, right-aligned (a caption under the value left a cell with
// none looking like it had lost a line; Andrew, 2026-09-21). The
// eyebrow row reserves the AntD link button's height on EVERY cell, so the
// values of neighbouring cells sit on one baseline whether or not a cell has
// an action (the read-only preview has none at all). `muted` is the
// placeholder treatment ("-"): still readable ink3 at
// this size, so it reads as absence, not as a value.
const FactCell = ({
  icon,
  label,
  action,
  value,
  muted = false,
  caption,
  surface,
}: {
  icon: ReactNode;
  label: string;
  action?: ReactNode;
  value: ReactNode;
  muted?: boolean;
  caption?: ReactNode;
  surface: string;
}) => (
  <div style={{ background: surface, padding: "14px 18px", minWidth: 0 }}>
    <Flex justify="space-between" align="center" gap={8} style={{ minHeight: 32 }}>
      <Flex align="center" gap={8} style={{ minWidth: 0, color: colors.ink2 }}>
        <span
          style={{
            width: 28,
            height: 28,
            borderRadius: 8,
            flexShrink: 0,
            background: ICON_TONES.navy.bg,
            color: ICON_TONES.navy.fg,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: textPx(14),
          }}
        >
          {icon}
        </span>
        <span
          title={label}
          style={{
            fontSize: textPx(12),
            fontWeight: 700,
            letterSpacing: 0.6,
            textTransform: "uppercase",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {label}
        </span>
      </Flex>
      {/* .fact-cell-action (index.css) paints the pencil ink rather than the
          modal-buttons navy; a theme override here would reach the trigger's
          own modal too and turn its Konfirmasi button ink. */}
      {action && <span className="fact-cell-action" style={{ flexShrink: 0 }}>{action}</span>}
    </Flex>
    <Flex justify="space-between" align="baseline" gap={12} style={{ marginTop: 4 }}>
      <div
        style={{
          ...VALUE_TEXT,
          ...(muted ? { color: colors.ink3 } : {}),
          minWidth: 0,
          overflowWrap: "anywhere",
        }}
      >
        {value}
      </div>
      {caption && (
        <div style={{ fontSize: textPx(12.5), color: colors.ink2, flexShrink: 0, textAlign: "right" }}>{caption}</div>
      )}
    </Flex>
  </div>
);

// The sale-level action in the page header wears the download buttons'
// chrome (white fill, the same border and pill) with red text and icon:
// shaped like its neighbours so it does not hang beside them, coloured so
// it still reads as the reversal it is (chris, 2026-10-17, after the quiet
// text variant looked unanchored next to two bordered buttons). AntD's
// default-button tokens carry the red, so hover keeps it and the disabled
// state still greys out through the shared disabled colour.
// The record tile's icon wears the colour of the status it records: the
// Voided badge's red on a voided sale, the Upgraded status's navy on the old
// half of an upgrade, so the tile pairs with the chip in the head.
const VOIDED_ACCENT = { icon: colors.error, tile: colors.errorTint };
const UPGRADED_ACCENT = { icon: colors.navy, tile: colors.navyTint };

const saleActionButtonTheme = {
  components: {
    Button: {
      defaultColor: colors.error,
      defaultHoverColor: colors.error,
      defaultActiveColor: colors.error,
      defaultHoverBorderColor: colors.error,
      defaultActiveBorderColor: colors.error,
    },
  },
};

export const CustomerPackageCard: React.FC<
  {
    customerPackage: CustomerPackage;
    isView?: boolean;
    recentlyUpgraded?: boolean;
    /** Where the sale-level action (Void, or Batalkan Upgrade on the new
     *  half of an upgrade) renders. The detail page hands over a slot in
     *  its header beside Unduh Invoice / Unduh Kwitansi: the action belongs
     *  with the other things done to the sale, not beside the price it used
     *  to crowd (chris, 2026-10-17, after seeing both mocked). The rules
     *  that decide the button stay here with the modals they open; without
     *  a slot (the Upgrade page's read-only previews) nothing renders. */
    actionsContainer?: HTMLElement | null;
  } & CardProps
> = ({
  customerPackage,
  isView = false, // If its only viewing purpose, don't show edit button
  recentlyUpgraded = false,
  actionsContainer = null,
  ...cardProps
}) => {
  const packageName = customerPackage?.package_deal.name;
  const unlimited = !!customerPackage?.package_deal.unlimited;
  const remainingCredits = getActualRemainingCredits(customerPackage);
  const remainingCreditsDisplay = unlimited
    ? translate("customer_package.unlimited")
    : `${remainingCredits}/${customerPackage?.total_credits}`;
  const onholdCredits = customerPackage?.onhold_credits ?? 0;
  const expiryDate = tzDate(customerPackage.expiry_date).endOf("day");
  const isExpired = isCustomerPackageExpired(customerPackage.expiry_date);
  // Is active if there is no inactive reason and the customer package is valid
  const isActive = (customerPackage?.remaining_credits || 0) > 0 && !isExpired && customerPackage.is_valid;
  // The end date can be changed while the package still has credits left,
  // held ones included (Kredit Tersisa, not Tersedia): a package whose
  // credits are all booked for upcoming classes is still in use, and the
  // studio may want to extend it (System Review C18). A membership has no
  // credit count and is never locked on it.
  const expiryLockedNoCredits =
    !unlimited && (customerPackage?.remaining_credits ?? 0) <= 0;
  const isNotEditable = expiryLockedNoCredits || !customerPackage.is_valid;
  // Masa Berlaku Produk: a first-use package whose clock has not started is
  // still AKTIF (it can be booked), it just has no expiry yet. The expiry cell
  // reads "-" and carries the day the clock starts on its own as its caption.
  const activation = packageActivation(customerPackage);
  const showAutoStart = activation.unactivated && isActive && !!activation.autoStartDate;

  const isVoided = !!customerPackage.voided_at;

  // Pembatasan Admin: void + expiry share one toggle, credits another; the
  // payment-method pencil stays open (bookkeeping, not a balance change). A
  // restricted control is plainly disabled, with no hover reason (Andrew,
  // 2026-09-03).
  const { isRestricted } = useAdminCapabilities();
  const voidRestricted = isRestricted(AdminCapability.PackageVoidExpiry);
  const creditRestricted = isRestricted(AdminCapability.CreditAdjustment);
  // A deactivated client's package is read-only (the server refuses too):
  // `locked` once nobody active at the studio uses it. A shared package whose
  // owner is deactivated stays editable while a co-sharer still uses it,
  // except adding co-sharers (the sharing modal) and upgrading (its own tab).
  // Plainly disabled, no hover reason; the page banner says why.
  const locked = customerPackage.in_use === false;
  const ownerDeactivated = customerPackage.owner_active === false;

  // LEGACY rows only. A voided sale carries who and when on the package
  // itself, so it needs no request; a package deleted before this feature
  // (is_valid false, voided_at null) still has only the status log. Nothing
  // is fetched for an active package or the read-only upgrade preview, and a
  // package inactive for another reason (expired, out of credits) never went
  // through the void action, so its list is simply empty.
  const { data: statusLogs } = getStatusLogsByCustomerPackageId(
    customerPackage.id,
    !isView && !customerPackage.is_valid && !isVoided
  );
  const latestStatusLog = statusLogs?.[0];

  // ── Void sale ──────────────────────────────────────────────────────────
  // A voided sale is kept, marked and dropped from every revenue figure; it
  // is never deleted. The server is the authority on whether this one may be
  // voided; `void_eligibility` mirrors those rules so the card can grey the
  // button and say why instead of failing on click. An older server sends
  // none, which reads as eligible and refuses on click as it always did.
  const eligibility = customerPackage.void_eligibility;
  // NOT `isActive`: a fully used or already expired package is exactly the
  // case a mistaken sale needs correcting in, and the deactivated-client
  // lock does not apply either (a wrong client is often deactivated first).
  const voidAllowed =
    !isView &&
    customerPackage.is_valid &&
    !isVoided &&
    !voidRestricted &&
    (eligibility?.ok ?? true);
  // What the button does when the server says no, by the KIND of no:
  //  * something somebody can still change: shown disabled with a hover note
  //    (a live booking the admin can cancel first; a past month a Manager
  //    can void where a plain Admin cannot);
  //  * something nobody can ever change (an upgrade half, a reward grant):
  //    not shown at all. A button that exists only to be disabled is noise,
  //    and the card does not offer actions that cannot apply.
  // A referral the sale converted never blocks: its unspent rewards are
  // voided with the sale, and a reward already used simply stays used with
  // the referral kept (eligibility.referral_kept; the dialog says which).
  // A capability restriction stays plainly disabled with no hover reason.
  const blocker = !isView && !isVoided && eligibility && !eligibility.ok ? eligibility.reason : null;
  const voidTooltip =
    !voidRestricted && blocker === "has_bookings"
      ? translate("void_hint.has_bookings")
      : !voidRestricted && blocker === "past_month"
        ? translate("void_hint.manager_only")
        : null;
  // A restricted admin sees the button disabled only where an unrestricted
  // one would see it at all; a sale nobody can ever void stays hidden for
  // both, so one package never gets two looks.
  const voidButtonShown =
    !isVoided &&
    customerPackage.is_valid &&
    (voidAllowed ||
      blocker === "has_bookings" ||
      blocker === "past_month" ||
      (voidRestricted && !blocker));

  // ── Cancel Upgrade ────────────────────────────────────────────────────
  // The NEW half of an upgrade can't be voided (the Void button is hidden
  // for it above); this is the action for the pair: the server voids this
  // package and puts the old one back. Same shape as void: the server's
  // `cancel_upgrade_eligibility` decides what the button does, the write is
  // the authority. ABSENT (older server, or not an upgrade half) reads as
  // hidden, the opposite of void's fallback, because there is no endpoint
  // to fall back to.
  const cancelEligibility = customerPackage.cancel_upgrade_eligibility ?? null;
  const cancelBlocker =
    cancelEligibility && !cancelEligibility.ok ? cancelEligibility.reason : null;
  const cancelUpgradeShown =
    !isView &&
    !isVoided &&
    customerPackage.is_valid &&
    !!cancelEligibility &&
    cancelBlocker !== "not_upgrade" &&
    cancelBlocker !== "membership";
  const cancelUpgradeAllowed =
    cancelUpgradeShown && !voidRestricted && !!cancelEligibility && cancelEligibility.ok;
  // Every fixable refusal has a hover note; a capability restriction stays
  // plainly disabled with none, like Void.
  const cancelUpgradeTooltip =
    voidRestricted || !cancelEligibility || !cancelBlocker
      ? null
      : cancelBlocker === "past_month"
        ? translate("cancel_upgrade_hint.past_month")
        : cancelBlocker === "used_sessions"
          ? cancelEligibility.used_sessions === 1
            ? translate("cancel_upgrade_hint.used_sessions_one")
            : translateWithVars("cancel_upgrade_hint.used_sessions", {
                count: cancelEligibility.used_sessions,
              })
          : cancelBlocker === "has_bookings"
            ? cancelEligibility.new_bookings === 1
              ? translate("cancel_upgrade_hint.has_bookings_one")
              : translateWithVars("cancel_upgrade_hint.has_bookings", {
                  count: cancelEligibility.new_bookings,
                })
            : cancelBlocker === "window" && cancelEligibility.window
              ? translateWithVars("cancel_upgrade_hint.window", {
                  className: cancelEligibility.window.className,
                  date: formatDate(
                    cancelEligibility.window.occurrence,
                    DEFAULT_DATE_FORMAT + ", HH:mm",
                  ),
                })
              : translate(`cancel_upgrade_hint.${cancelBlocker}`);

  // A sale paid with a loyalty or referral discount voucher: the admin picks
  // what happens to that voucher, return it to the client or void it
  // (Andrew, 2026-09-25). A discount code is not a voucher and gets no
  // choice, and a referred friend's intro voucher belongs to the referral
  // revert rather than to this question, which is why the server decides
  // (voucher_question) rather than the card reading the discount source.
  const voucherSource = discountSourceOf(customerPackage);
  // A server that answers eligibility decides, INCLUDING a null (no question:
  // the friend's intro voucher is being unwound with the referral revert). The
  // discount-source fallback is only for an older server that sends no
  // eligibility at all; `??` here would re-ask the question the server just
  // said not to ask.
  const voucherQuestion = eligibility
    ? eligibility.voucher_question
    : voucherSource === DiscountSource.Loyalty || voucherSource === DiscountSource.Referral
      ? voucherSource === DiscountSource.Loyalty
        ? ("loyalty" as const)
        : ("referral" as const)
      : null;

  const { mutate: voidPackage, isPending: voiding } = useVoidCustomerPackage(
    // The sale already has taught sessions. Reopen the same modal showing the
    // count, keeping what was already typed, and retry acknowledged.
    (usedSessions, form) => voidWarning(usedSessions, form),
  );
  const { mutate: unvoidPackage, isPending: unvoiding } = useUnvoidCustomerPackage();
  const { mutate: cancelUpgrade, isPending: cancellingUpgrade } = useCancelUpgrade();

  const [voidForm] = Form.useForm();

  // The referral this sale converted (detail fetch only). The void reopens it
  // and voids the reward vouchers it issued, unless a reward was already
  // used, in which case the referral stays converted; the dialog says which.
  const convertedReferral = customerPackage.referral_conversion ?? null;
  const referralNoteKey = eligibility?.referral_kept
    ? "messages.warning.void_customer_package.referral_kept_note"
    : "messages.warning.void_customer_package.referral_note";

  // `retry` is set when the server asked for the used-sessions acknowledgment,
  // so the form keeps the reason and note the admin already chose.
  const voidWarning = (usedSessions?: number, retry?: VoidCustomerPackageForm) => {
    const knownUsed = usedSessions ?? eligibility?.used_sessions ?? 0;
    showDestructiveConfirm({
      title: translate("messages.warning.void_customer_package.title"),
      message: translate("messages.warning.void_customer_package.content"),
      width: 460,
      extra: (
        <Form
          form={voidForm}
          layout="vertical"
          // preserve={false} is load-bearing, not tidiness. The form instance
          // outlives the static modal, so with AntD's default the store keeps
          // whatever was typed and BEATS initialValues on the next mount: a
          // reason the admin abandoned would come back pre-selected on the
          // next open, with the confirm button already enabled. Clearing it on
          // unmount makes initialValues the single source, which is what the
          // retry below relies on.
          preserve={false}
          // A re-open after the used-sessions refusal keeps what was typed.
          initialValues={
            retry
              ? { reason: retry.reason, note: retry.note, voucherAction: retry.voucherAction ?? "return" }
              : { voucherAction: "return" }
          }
          style={{ marginTop: 16 }}
        >
          {/* The things worth a second look before confirming, each on its
              own amber line: a past month's figures changing (Managers only
              reach this), classes becoming complimentary, and a referral
              this sale converted being reopened. */}
          {[
            ...(eligibility?.past_month
              ? [translate("messages.warning.void_customer_package.past_month_notice")]
              : []),
            ...(knownUsed > 0
              ? [
                  translateWithVars("messages.warning.void_customer_package.used_sessions", {
                    count: knownUsed,
                  }),
                ]
              : []),
            ...(convertedReferral
              ? [translateWithVars(referralNoteKey, { referrer: convertedReferral.referrer_name })]
              : []),
          ].map((line) => (
            <Flex
              key={line}
              gap={8}
              align="flex-start"
              style={{
                marginBottom: 16,
                padding: "10px 12px",
                borderRadius: 8,
                background: ICON_TONES.amber.bg,
              }}
            >
              <InfoCircleOutlined style={{ fontSize: textPx(16), color: ICON_TONES.amber.fg, marginTop: 2 }} />
              <Typography.Text style={{ fontSize: textPx(13), color: ICON_TONES.amber.fg }}>{line}</Typography.Text>
            </Flex>
          ))}
          <Form.Item
            name="reason"
            label={translate("messages.warning.void_customer_package.reason_label")}
            style={{ marginBottom: 16 }}
          >
            {/* Option cards (.choice-cards in index.css): the WHOLE card is
                the radio's label, so the click target is the card, not the
                dot. There is no default, so the confirm stays disabled until one is
                picked (requireChoice below). */}
            <Radio.Group className="choice-cards" style={{ width: "100%" }}>
              <Flex vertical gap={8}>
                {Object.values(VoidReason).map((reason) => (
                  <Radio key={reason} value={reason}>
                    <Typography.Text style={{ fontWeight: 500 }}>
                      {translate(`void_reason.${reason}`)}
                    </Typography.Text>
                  </Radio>
                ))}
              </Flex>
            </Radio.Group>
          </Form.Item>
          {/* Room under the counter that showCount hangs below the box, so
              the dialog's footer does not sit on top of it. */}
          <Form.Item name="note" style={{ marginBottom: 12 }}>
            <Input.TextArea
              rows={2}
              maxLength={200}
              showCount
              placeholder={translate("messages.warning.void_customer_package.note_placeholder")}
            />
          </Form.Item>
          {voucherQuestion && (
            <Flex vertical gap={10} style={{ marginTop: 16 }}>
              <Typography.Text strong>
                {translateWithVars("messages.warning.void_customer_package.voucher_question", {
                  source: translate(
                    voucherQuestion === "loyalty"
                      ? "purchaseDiscount.source_loyalty"
                      : "purchaseDiscount.source_referral",
                  ),
                })}
              </Typography.Text>
              <Form.Item name="voucherAction" noStyle>
                <Radio.Group className="choice-cards" style={{ width: "100%" }}>
                  <Flex vertical gap={8}>
                    {(["return", "void"] as const).map((action) => (
                      <Radio key={action} value={action}>
                        <Flex vertical gap={2}>
                          <Typography.Text style={{ fontWeight: 500 }}>
                            {translate(`messages.warning.void_customer_package.voucher_${action}`)}
                          </Typography.Text>
                          <Typography.Text style={{ fontSize: textPx(13), color: colors.ink2 }}>
                            {translate(`messages.warning.void_customer_package.voucher_${action}_hint`)}
                          </Typography.Text>
                        </Flex>
                      </Radio>
                    ))}
                  </Flex>
                </Radio.Group>
              </Form.Item>
            </Flex>
          )}
        </Form>
      ),
      // A reason is mandatory, and "other" is only a reason once it says what
      // happened. The server enforces both; this keeps the button honest.
      requireChoice: {
        form: voidForm,
        name: "reason",
        requiredWhen: { choiceIs: VoidReason.Other, field: "note" },
      },
      confirmLabel: translate("button.void_purchase"),
      onConfirm: () => {
        const values = voidForm.getFieldsValue() as {
          reason: VoidReason;
          note?: string;
          voucherAction?: SpentVoucherAction;
        };
        voidPackage({
          customerPackageId: customerPackage.id,
          customerId: customerPackage.customer_id,
          reason: values.reason,
          note: values.note?.trim() || undefined,
          voucherAction: voucherQuestion ? (values.voucherAction ?? "return") : undefined,
          // The admin has acknowledged the comps whenever this modal actually
          // SHOWED them, which is the usual case: the package read already
          // carries used_sessions. The retry path covers the other case, a
          // booking that auto-completed between that read and this click, so
          // the server still gets to refuse once and be told.
          acknowledgeUsedSessions: !!retry || knownUsed > 0,
        });
      },
    });
  };

  // No form: the reason is fixed server-side and there is no note. The
  // amber lines name what the admin is about to move, the same pattern as
  // the void dialog: the fee leaving revenue (and needing a refund by hand),
  // the credits and bookings going back, the old package already expired, a
  // referral reopening or kept, and a past month's figures changing. A line
  // about nothing is left out: a zero fee (a bigger tier priced at or below
  // the old one) has nothing to refund, and the expiry note is about the
  // returned credits, so it needs some to return.
  const cancelUpgradeWarning = () => {
    const e = cancelEligibility;
    if (!e) return;
    const lines = [
      ...(e.past_month
        ? [translate("messages.warning.cancel_upgrade.past_month_notice")]
        : []),
      ...(e.fee > 0
        ? [
            translateWithVars("messages.warning.cancel_upgrade.fee_notice", {
              amount: formatCurrency(e.fee),
            }),
          ]
        : []),
      translateWithVars("messages.warning.cancel_upgrade.credits_notice", {
        count: e.credits_returning,
        name: e.previous_package_name,
      }),
      ...(e.bookings_returning > 0
        ? [
            translateWithVars("messages.warning.cancel_upgrade.bookings_notice", {
              count: e.bookings_returning,
            }),
          ]
        : []),
      ...(e.a_expired && e.credits_returning > 0
        ? [translate("messages.warning.cancel_upgrade.expired_notice")]
        : []),
      ...(e.b_expiry_edited
        ? [translate("messages.warning.cancel_upgrade.expiry_edit_notice")]
        : []),
      ...(convertedReferral && (e.referral_reopens || e.referral_kept)
        ? [
            translateWithVars(
              e.referral_kept
                ? "messages.warning.void_customer_package.referral_kept_note"
                : "messages.warning.void_customer_package.referral_note",
              { referrer: convertedReferral.referrer_name },
            ),
          ]
        : []),
    ];
    showDestructiveConfirm({
      title: translate("messages.warning.cancel_upgrade.title"),
      message: translate("messages.warning.cancel_upgrade.content"),
      width: 460,
      extra: (
        <Flex vertical style={{ marginTop: 16 }}>
          {lines.map((line) => (
            <Flex
              key={line}
              gap={8}
              align="flex-start"
              style={{
                marginBottom: 12,
                padding: "10px 12px",
                borderRadius: 8,
                background: ICON_TONES.amber.bg,
              }}
            >
              <InfoCircleOutlined style={{ fontSize: textPx(16), color: ICON_TONES.amber.fg, marginTop: 2 }} />
              <Typography.Text style={{ fontSize: textPx(13), color: ICON_TONES.amber.fg }}>{line}</Typography.Text>
            </Flex>
          ))}
        </Flex>
      ),
      confirmLabel: translate("button.cancel_upgrade"),
      onConfirm: () =>
        cancelUpgrade({
          customerPackageId: customerPackage.id,
          customerId: customerPackage.customer_id,
        }),
    });
  };

  const undoVoidWarning = () =>
    showCautionConfirm({
      title: translate("messages.warning.undo_void.title"),
      message: translate("messages.warning.undo_void.content"),
      confirmLabel: translate("button.undo_void"),
      onConfirm: () =>
        unvoidPackage({
          customerPackageId: customerPackage.id,
          customerId: customerPackage.customer_id,
        }),
    });

  // Active members only; revoked rows ride the same payload for the history.
  const sharedNames = (customerPackage.members ?? [])
    .filter((m) => !m.deleted_at)
    .map((m) => m.customer?.user?.full_name ?? "-");
  // One member is named; two or more read as a count (Andrew, 2026-09-21).
  // The names are in the sharing modal. Nobody yet reads "-", the same
  // placeholder the strip's other cells print (chris, 2026-10-17): the share
  // icon beside the label already says the action exists.
  const sharedValue =
    sharedNames.length === 0
      ? "-"
      : sharedNames.length === 1
        ? sharedNames[0]
        : translateWithVars("package_sharing.member_count", { n: sharedNames.length });

  // The strip's cells paint the SAME surface as the card (white active, soft
  // inactive), and the grid's 1px gap over the line colour is what draws the
  // hairlines between them, horizontally and vertically alike, so the strip
  // needs no divider logic when it goes from four across to 2x2 to a column.
  // That only holds while every row is full, which the column rules in
  // index.css guarantee.
  const surface = isActive ? colors.card : colors.bgSoft;
  // "Diskon −Rp 300.000 · Loyalty" under the price; null without a discount.
  const discountNote = discountSummary(customerPackage);
  const isReward = rewardSourceOf(customerPackage.package_deal) !== null;

  const cells: ReactNode[] = [
    // Editing credits stays available on an exhausted package (restoring
    // credits is a primary use case), only an invalidated one blocks it. The
    // on-hold count is a visible caption, not hover-only, and only when
    // there is something held.
    <FactCell
      key="credits"
      surface={surface}
      icon={<DollarCircleOutlined />}
      label={translate("label.available_session")}
      value={remainingCreditsDisplay}
      caption={
        !unlimited
          ? [
              onholdCredits > 0 ? translateWithVars("label.onhold_caption", { n: onholdCredits }) : null,
              // Credits raised past total − used: the classes on them earn Rp 0.
              (customerPackage.goodwill_credits ?? 0) > 0
                ? translateWithVars("label.goodwill_caption", { n: customerPackage.goodwill_credits })
                : null,
            ]
              .filter(Boolean)
              .join(" · ") || undefined
          : undefined
      }
      action={
        !isView &&
        !unlimited && (
          <ConfigProvider theme={modalButtonsTheme}>
            <UpdateCustomerPackageCreditsModal
              customerPackage={customerPackage}
              disabled={!customerPackage.is_valid}
              restricted={creditRestricted || locked}
            />
          </ConfigProvider>
        )
      }
    />,
  ];

  // Always shown: a package with no end date never expires (the booking
  // cores' reading), and the pencil is how the studio gives it one (System
  // Review C16).
  cells.push(
    // Update Expiry Date is disabled once no credit is left (held ones
    // count) or the package is no longer valid.
    <FactCell
      key="expiry"
      surface={surface}
      icon={<ScheduleOutlined />}
      label={translate("label.expiry_date")}
      muted={activation.unactivated || !customerPackage.expiry_date}
      // Plain date, no status dot: AKTIF / TIDAK AKTIF in the head already
      // says whether the package is live (Andrew, 2026-09-21).
      value={
        activation.unactivated || !customerPackage.expiry_date
          ? "-"
          : formatDate(expiryDate)
      }
      // The one date an unactivated package has. NBSP-joined so the date
      // never wraps apart.
      caption={
        showAutoStart
          ? translateWithVars("label.auto_start_on", {
              date: formatDate(activation.autoStartDate!, "DD MMM YYYY").replace(/ /g, " "),
            })
          : undefined
      }
      action={
        !isView && (
          <ConfigProvider theme={modalButtonsTheme}>
            <UpdateCustomerPackageModal
              packageId={customerPackage.id}
              disabled={isNotEditable}
              disabledReason={
                !customerPackage.is_valid
                  ? translate("popover.expiry_package_invalid")
                  : undefined
              }
              restricted={voidRestricted || locked}
              firstUse={activation.unactivated}
              lastBookedClassAt={customerPackage.last_booked_class_at}
            />
          </ConfigProvider>
        )
      }
    />
  );

  cells.push(
    // Editable any time, except on a reward credit, which had no payment to
    // record (greyed out rather than hidden, so the strip keeps its shape).
    <FactCell
      key="payment"
      surface={surface}
      icon={<CreditCardOutlined />}
      label={translate("paymentMethods.col_payment_method")}
      muted={!customerPackage.payment_method_name}
      value={customerPackage.payment_method_name ?? "-"}
      action={
        !isView && (
          <ConfigProvider theme={modalButtonsTheme}>
            <UpdateCustomerPackagePaymentMethodModal
              packageId={customerPackage.id}
              currentPaymentMethodId={customerPackage.payment_method_id}
              currentPaymentMethodName={customerPackage.payment_method_name}
              // isVoided, not !is_valid: the server refuses package edits only
              // on a VOIDED sale, so a package invalidated before this feature
              // keeps the bookkeeping pencil it has always had.
              disabled={isReward || locked || isVoided}
            />
          </ConfigProvider>
        )
      }
    />
  );

  // Only for a tier the studio actually marked shareable, so nothing new
  // appears on an ordinary package (three cells, no placeholder; Andrew,
  // 2026-09-21). Reward tiers are excluded server-side and never reach here
  // shareable.
  if (customerPackage.package_deal.is_shareable) {
    cells.push(
      <FactCell
        key="sharing"
        surface={surface}
        icon={<TeamOutlined />}
        label={translate("package_sharing.shared_with")}
        muted={sharedNames.length === 0}
        value={sharedValue}
        action={
          !isView && (
            <ConfigProvider theme={modalButtonsTheme}>
              <ManagePackageSharingModal
                customerPackage={customerPackage}
                // Same rule as credits: an invalidated package can no
                // longer be granted to anyone new.
                disabled={!customerPackage.is_valid || creditRestricted || locked}
                addDisabled={ownerDeactivated}
              />
            </ConfigProvider>
          )
        }
      />
    );
  }

  return (
    <Card
      {...cardProps}
      title={
        // Wraps so the price + delete cluster drops under the name on a narrow
        // screen instead of squeezing it.
        <Flex justify="space-between" align="center" wrap="wrap" gap={12} style={{ rowGap: 8 }}>
          {/* Badges as flex siblings of the title, not inline children:
              inline-flex badges baseline-align inside a Title and sit below
              its optical center. */}
          <Flex align="center" gap={8} wrap="wrap" style={{ minWidth: 0, flex: "1 1 260px" }}>
            <Title
              level={4}
              style={{
                margin: 0,
                fontFamily: "var(--font-display)",
                letterSpacing: "-0.01em",
              }}
            >
              {packageName}
            </Title>
            {/* A voided sale REPLACES the validity badge rather than sitting
                beside it: both would be red and both would say the package is
                off, and "Voided" is the more specific of the two. A first-use
                package whose clock has not started reads Active like any other
                bookable package (Andrew, 2026-09-12; it used to show an amber
                BELUM AKTIF). */}
            {isVoided ? (
              <StatusBadge
                tone={INACTIVE_REASON_TONE[InactiveReason.MANUALLY_MARKED_INVALID]}
                label={INACTIVE_REASON_LABELS[InactiveReason.MANUALLY_MARKED_INVALID]}
              />
            ) : (
              <ValidTag isValid={isActive} />
            )}
            {/* Same badge the Paket/Membership row shows, so a package does
                not appear to change identity when an admin opens it. Who it
                is shared with is named in the strip below. */}
            {(customerPackage.is_shared_with_me || sharedNames.length > 0) && (
              <StatusBadge
                tone="progress"
                label={SHARED_LABEL}
                dot={false}
              />
            )}
            {/* "Upgrade" (a label: this package came from an upgrade), not
                "Upgraded" (the old package's inactive reason). */}
            {recentlyUpgraded && <StatusBadge tone="navy" label={UPGRADE_LABEL} dot={false} />}
            {isReward && (
              <StatusBadge
                tone="purple"
                label={REWARD_LABEL}
                dot={false}
              />
            )}
          </Flex>
          {/* The price stands alone on the head, right-aligned under nothing
              but itself, so it reads as a fact. The read-only preview shows
              no price. */}
          {!isView && (
            <Flex vertical align="flex-end" style={{ flexShrink: 0, marginLeft: "auto" }}>
              {/* The discount, when there was one, sits right under the
                  price it explains, in the same note form as the Transaksi
                  tab. */}
              <Title
                level={5}
                style={{ margin: 0, fontVariantNumeric: "tabular-nums" }}
              >
                {/* amountPaid, not purchase_price: an upgrade row stores the
                    new package's full value but only its fee was collected. */}
                {formatCurrency(amountPaid(customerPackage))}
              </Title>
              {/* This row's own snapshot, not today's settings: the card
                  shows what the client was charged, which may predate a
                  tax change, exactly as its invoice does. */}
              <InclusiveTaxNote
                amount={amountPaid(customerPackage)}
                enabled={customerPackage.tax_enabled_snapshot}
                mode={customerPackage.tax_mode_snapshot}
                rate={customerPackage.tax_rate_snapshot}
                label={customerPackage.tax_label_snapshot}
              />
              {discountNote && (
                <span
                  style={{
                    fontSize: textPx(12),
                    color: colors.ink2,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {discountNote}
                </span>
              )}
            </Flex>
          )}
          {/* The sale-level action, rendered into the page header's slot
              (see actionsContainer). Labelled rather than icon-only, so it
              names its action. Gone once the sale IS voided (the badge says
              so and the voided line carries the undo) and gone when nothing
              could ever make the sale voidable. The span lets a disabled
              button still show its tooltip. Dressed like the download buttons
              beside it with red text (saleActionButtonTheme); filled red
              stays reserved for the confirm inside a destructive dialog. */}
          {!isView &&
            actionsContainer &&
            voidButtonShown &&
            createPortal(
              <ConfigProvider theme={saleActionButtonTheme}>
                <Tooltip title={voidTooltip ?? undefined} placement="topRight">
                  <span>
                    <Button
                      icon={<StopOutlined />}
                      loading={voiding}
                      onClick={voidAllowed ? () => voidWarning() : undefined}
                      disabled={!voidAllowed || voiding}
                    >
                      {translate("button.void_purchase")}
                    </Button>
                  </span>
                </Tooltip>
              </ConfigProvider>,
              actionsContainer
            )}
          {/* The new half of an upgrade gets this instead of Void: same
              slot, same dress, same tooltip-through-a-span trick for the
              disabled state. */}
          {!isView &&
            actionsContainer &&
            cancelUpgradeShown &&
            createPortal(
              <ConfigProvider theme={saleActionButtonTheme}>
                <Tooltip title={cancelUpgradeTooltip ?? undefined} placement="topRight">
                  <span>
                    <Button
                      icon={<RollbackOutlined />}
                      loading={cancellingUpgrade}
                      onClick={cancelUpgradeAllowed ? () => cancelUpgradeWarning() : undefined}
                      disabled={!cancelUpgradeAllowed || cancellingUpgrade}
                    >
                      {translate("button.cancel_upgrade")}
                    </Button>
                  </span>
                </Tooltip>
              </ConfigProvider>,
              actionsContainer
            )}
        </Flex>
      }
      style={{
        marginBottom: 30,
        backgroundColor: surface,
      }}
    >
      {/* The column count comes from the cell count and the card's width
          (index.css, .package-fact-strip): four across, 2x2, then one column.
          The host div is the size container the CSS measures. */}
      <div className="package-fact-strip-host">
        <div
          className="package-fact-strip"
          style={{
            display: "grid",
            ["--cells" as string]: cells.length,
            gap: 1,
            background: colors.line,
            border: `1px solid ${colors.line}`,
            borderRadius: 12,
            overflow: "hidden",
          }}
        >
          {cells}
        </div>
      </div>
      {/* What happened to the sale, under its facts: the record of a void or
          an upgrade, in the admin's one note shape (components/WarningBox) in
          its grey tone, the way a refund sits in a Stripe or Shopify record's
          history. Not red: the Voided badge in the head already carries the
          status colour, and a loose red sentence here read as an error
          (chris, 2026-10-17). Fact line = who, when, why; the studio's note
          on the quieter line beneath; Undo as the one action. A voided sale
          carries who, when and why on the package itself; a row deleted
          before this feature has only its status log. */}
      {isVoided && (
        <WarningBox
          tone="neutral"
          icon={StopOutlined}
          accent={VOIDED_ACCENT}
          style={{ marginTop: 16 }}
          items={[
            {
              fact: `${translate("label.voided_by_prefix")} ${customerPackage.voided_by_name ?? "-"} ${translate(
                "label.voided_on_prefix"
              )} ${formatDate(customerPackage.voided_at!, DEFAULT_DATE_FORMAT + ", HH:mm")}${
                customerPackage.void_reason
                  ? ` · ${translate(`void_reason.${customerPackage.void_reason}`)}`
                  : ""
              }`,
              action: customerPackage.void_note ?? undefined,
            },
          ]}
          extra={
            /* Same day only, the window the server enforces. A restricted
               admin gets it plainly disabled, like the Void button. Never on
               the new half of a cancelled upgrade: that void has no undo (the
               old package is live again beside it); the way back is to
               upgrade again. The server refuses it too. */
            !isView &&
            customerPackage.void_reason !== UPGRADE_CANCELLED_VOID_REASON &&
            sameJakartaDay(customerPackage.voided_at) && (
              <Button
                size="small"
                loading={unvoiding}
                disabled={unvoiding || voidRestricted}
                onClick={voidRestricted ? undefined : undoVoidWarning}
              >
                {translate("button.undo_void")}
              </Button>
            )
          }
        />
      )}
      {/* Legacy rows only, and only while the package is still off: React
          Query keeps the last fetched log after the query is disabled, so
          without the is_valid guard an undone void showed the undo's own log
          row as a "voided by" line under a live Active badge. */}
      {!isVoided &&
        !customerPackage.is_valid &&
        latestStatusLog &&
        !latestStatusLog.new_is_valid &&
        (latestStatusLog.reason === UPGRADED_STATUS_REASON ? (
          /* The OLD half of an upgrade: not voided, upgraded. Says where it
             went (the log's note is the new tier's name, the same record the
             History row reads) and where to cancel from, since the action
             lives on the new package. */
          <WarningBox
            tone="neutral"
            icon={ArrowUpOutlined}
            accent={UPGRADED_ACCENT}
            style={{ marginTop: 16 }}
            items={[
              {
                fact: translateWithVars("label.upgraded_to_fact", { name: latestStatusLog.note ?? "" }),
                action: translate("label.upgraded_to_action"),
              },
            ]}
          />
        ) : (
          <WarningBox
            tone="neutral"
            icon={StopOutlined}
            accent={VOIDED_ACCENT}
            style={{ marginTop: 16 }}
            items={[
              {
                fact: `${translate("label.voided_by_prefix")} ${latestStatusLog.actor.full_name} ${translate(
                  "label.voided_on_prefix"
                )} ${formatDate(latestStatusLog.created_at, DEFAULT_DATE_FORMAT + ", HH:mm")}`,
              },
            ]}
          />
        ))}
    </Card>
  );
};
